import React, { useCallback, useEffect, useState } from 'react';
import { MiniSeekBar } from '@/components/common/MiniSeekBar';
import { useUrlWaveform } from '@/hooks/useUrlWaveform';
import { formatTimecode } from '@/utils/formatUtils';

export interface WaveformAudioPlayerProps {
  src: string;
  className?: string;
}

/** Audio player whose seek bar is the file's waveform (play/pause + time + MiniSeekBar). */
export const WaveformAudioPlayer: React.FC<WaveformAudioPlayerProps> = ({ src, className }) => {
  const [audio, setAudio] = useState<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const wave = useUrlWaveform(src);

  useEffect(() => {
    if (!audio) return undefined;
    const sync = () => { setTime(audio.currentTime); setDuration(Number.isFinite(audio.duration) ? audio.duration : 0); };
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onError = () => setError(audio.error?.message || 'This audio could not be played.');
    audio.addEventListener('timeupdate', sync);
    audio.addEventListener('loadedmetadata', sync);
    audio.addEventListener('durationchange', sync);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('ended', onPause);
    audio.addEventListener('error', onError);
    return () => {
      audio.removeEventListener('timeupdate', sync);
      audio.removeEventListener('loadedmetadata', sync);
      audio.removeEventListener('durationchange', sync);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('ended', onPause);
      audio.removeEventListener('error', onError);
    };
  }, [audio]);

  const toggle = useCallback(() => {
    if (!audio) return;
    if (audio.paused) {
      audio.play().catch((e: unknown) => setError(e instanceof Error ? e.message : 'Playback was blocked.'));
    } else {
      audio.pause();
    }
  }, [audio]);

  return (
    <div className={`flex items-center gap-2 w-full ${className ?? ''}`}>
      <audio ref={setAudio} src={src} preload="metadata" />
      <button
        type="button"
        onClick={toggle}
        className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full bg-white/15 hover:bg-white/25 text-white"
        title={playing ? 'Pause' : 'Play'}
        aria-label={playing ? 'Pause' : 'Play'}
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
          {playing ? <path d="M6 4h4v16H6zM14 4h4v16h-4z" /> : <path d="M7 4l13 8-13 8z" />}
        </svg>
      </button>
      <div className="flex-1 min-w-0">
        {error ? (
          <span className="text-xs text-red-300">{error}</span>
        ) : audio ? (
          <MiniSeekBar
            videoEl={audio}
            peaks={wave?.peaks ?? null}
            peaksCoverage={wave?.coverage ?? 1}
            trailing={`${formatTimecode(time)} / ${formatTimecode(duration)}`}
          />
        ) : null}
      </div>
    </div>
  );
};
